<template>
  <div>
    <v-dialog
      v-model="showDialog"
      :persistent="isUploading"
      max-width="750"
      :overlay-opacity="0.8"
      content-class="overflow-hidden"
    >
      <div class="upload-modal">
        <UploadSidebar
          v-if="isImg"
          :active-func="activeFunc"
          :disabled="isUploading"
          @turnFunc="turnFuncHandler"
        />
        <v-card color="#eeeeee" width="100%" elevation="0">
          <v-toolbar
            class="text-center"
            dense
            color="#eeeeee"
            elevation="0"
          >
            <v-btn
              v-if="imageSrcs.length && !imageSrcToEdit"
              icon
              :disabled="isUploading"
              @click="resetData"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn icon :disabled="isUploading" @click="showDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider />
          <div class="upload-modal__content">
            <template v-if="showDialog">
              <UploadCropper
                v-if="imageSrcs.length"
                :image-src="imageSrcs[0]"
                :is-img="isImg"
                :active-func="activeFunc"
                :options="options"
                :uploaded-files="uploadedFiles"
                :source-type="sourceType"
                :source-id="sourceId"
                :is-multiply-sizes-upload="isMultiplySizesUpload"
                :attachment-type="attachmentType"
                :is-return-file-without-upload="isReturnFileWithoutUpload"
                @removeFile="removeFile"
                @fileUploaded="fileUploadedHandler"
                @update:uploading="isUploading = $event"
              />
              <DragAndDropArea
                v-else
                :skip-upload="skipUpload"
                :accept-types="options.accept"
                :multiple="options.multiple"
                @onUpload="onUploadHandler"
              />
            </template>
          </div>
        </v-card>
      </div>
    </v-dialog>
    <slot />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';

import { Nullable } from '@/utils/types';
import { IUploadFile, IUploadOptions } from '@/bundles/Upload/interfaces';
import { CropperFuncNamesEnum } from '@/bundles/Upload/helpers';

import UploadSidebar from '@/bundles/Upload/components/UploadSidebar.vue';
import DragAndDropArea from '@/bundles/Upload/components/DragAndDropArea.vue';
import UploadCropper from '@/bundles/Upload/components/UploadCropper.vue';

interface IUpload {
  uploadedFiles: File[];
  imageSrcs: string[];
  fileReader: Nullable<FileReader>;
  activeFunc: CropperFuncNamesEnum;
  isImg: boolean;
  isUploading: boolean;
}

export default Vue.extend({
  name: 'Upload',

  components: {
    UploadCropper,
    DragAndDropArea,
    UploadSidebar
  },

  props: {
    value: {
      type: Boolean,
      required: true
    },
    options: {
      type: Object as PropType<IUploadOptions>,
      default: () => ({ accept: 'image/*' })
    },
    sourceType: {
      type: String,
      default: null
    },
    sourceId: {
      type: [String, Number],
      default: null
    },
    skipUpload: Boolean, // returns original files without uploading
    isReturnFileWithoutUpload: Boolean, // returns File of cropped image without uploading
    imageSrcToEdit: {
      type: String,
      default: null
    },
    isMultiplySizesUpload: Boolean,
    attachmentType: {
      type: String,
      default: null,
    }
  },

  data: (): IUpload => ({
    uploadedFiles: [],
    imageSrcs: [],
    fileReader: null,
    activeFunc: CropperFuncNamesEnum.crop,
    isImg: false,
    isUploading: false,
  }),

  computed: {
    showDialog: {
      get (): boolean {
        return this.value;
      },
      set (value: boolean) {
        this.$emit('input', value);
      }
    }
  },

  watch: {
    showDialog: {
      handler (value: boolean) {
        if (!value) {
          this.resetData();
          return;
        }

        if (this.imageSrcToEdit) {
          this.isImg = true;
          this.imageSrcs = [this.imageSrcToEdit];
          this.turnFuncHandler(CropperFuncNamesEnum.crop);
        }
      },
      immediate: true
    },
  },

  methods: {
    resetData () {
      this.isUploading = false;
      this.imageSrcs = [];
      this.uploadedFiles = [];
      this.activeFunc = CropperFuncNamesEnum.crop;
      this.isImg = false;
    },
    removeFile (index: number) {
      if (this.uploadedFiles.length === 1) {
        this.resetData();
        return;
      }

      this.imageSrcs.splice(index, 1);
      this.uploadedFiles.splice(index, 1);
    },
    getImageDimensions (file: File): Promise<{ width: number; height: number }> {
      return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
          reject(new Error('Not an image file'));
          return;
        }
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
          URL.revokeObjectURL(img.src);
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    },
    async onUploadHandler ({ urls, files }: { urls: string[]; files: File[] }) {
      if (this.skipUpload) {
        this.$emit('fileUploaded', { originalFiles: files });
        this.showDialog = false;
        return;
      }

      const filesDimensions = await Promise.all(files.map(file => this.getImageDimensions(file).catch(() => null)));
      const maxMegapixels = 25;
      const tooLargeImages = filesDimensions.filter(dim => dim && (dim.width * dim.height) / 1_000_000 > maxMegapixels);

      if (tooLargeImages.length) {
        this.addNotification({
          title: 'Error',
          message: `We can’t upload files larger than ${maxMegapixels} megapixels. Please choose an image with a lower resolution.`,
          status: 0
        });
        return;
      }

      this.isImg = files.length > 1 ? false : new RegExp('image/*', 'i').test(files[0].type);

      const sizeMb = 20;
      const maxSizeInBytes = sizeMb * 1024 * 1024;
      const imgTooBig = this.isImg && files[0].size > maxSizeInBytes;
      const isSomeOfImagesTooBig = this.isMultiplySizesUpload && files.some(file => file.size > maxSizeInBytes); // this.isMultiplySizesUpload - only images can be uploaded
      if (imgTooBig || isSomeOfImagesTooBig) {
        this.addNotification({
          title: 'Error',
          message: `We can’t upload files this large. Please choose images under ${sizeMb} MB.`,
          status: 0
        });
        return;
      }

      this.imageSrcs = urls;
      this.uploadedFiles = files;

      if (!this.isImg && this.isReturnFileWithoutUpload) {
        this.$emit('file:returned', files[0]);
        this.showDialog = false;
        return;
      }

      if (this.isImg) {
        this.turnFuncHandler(CropperFuncNamesEnum.crop);
      }
    },
    turnFuncHandler (name: CropperFuncNamesEnum) {
      this.activeFunc = name;
    },
    fileUploadedHandler ({ files, originalFiles, file }: { files: IUploadFile[], originalFiles: File[]; file?: File } = {
      files: [],
      originalFiles: []
    }) {
      if (this.isReturnFileWithoutUpload) {
        this.$emit('file:returned', file);
        this.showDialog = false;
        return;
      }

      this.$emit('fileUploaded', { files, originalFiles });
      this.showDialog = false;
    },
  }
});
</script>

<style lang="scss" scoped>
.upload-modal {
  display: flex;
  background-color: #eeeeee;
  height: 500px;
}

.upload-modal__content {
  height: calc(100% - 48px); // 48px - toolbar height
}
</style>
